import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const BULK_API =
    `${process.env.REACT_APP_PRODUCTION_API_URL}backend/api/file-upload/bulk`;
const SAVE_API =
    `${process.env.REACT_APP_PRODUCTION_API_URL}backend/api/file-upload/save`;

const getHeaders = () => ({
    headers: {
        "x-api-key": localStorage.getItem("token"),
        "Content-Type": "application/json",
    },
    withCredentials: true,
});

export default function BulkFileUploader() {
    const [files, setFiles] = useState([]);
    const [uploaded, setUploaded] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [progress, setProgress] = useState({});
    const [uploadStatus, setUploadStatus] = useState("");

    const uploadAll = async () => {
        if (!files.length) return;

        setShowModal(true);
        setUploadStatus("uploading");
        setProgress({});
        setUploaded([]);

        const initialProgress = {};
        files.forEach(file => {
            initialProgress[file.name] = 0;
        });
        setProgress(initialProgress);

        try {
            const { data: signedFiles } = await axios.post(
                BULK_API,
                files.map((f) => ({ name: f.name, type: f.type })),
                getHeaders()
            );
            const successUploads = [];
            await Promise.all(
                signedFiles.map(async (item, index) => {
                    try {
                        await axios.put(item.uploadUrl, files[index], {
                            headers: {
                                "Content-Type": files[index].type,
                            },
                            onUploadProgress: (e) => {
                                setProgress((prev) => ({
                                    ...prev,
                                    [files[index].name]: Math.round((e.loaded * 100) / e.total),
                                }));
                            },
                        });
                        successUploads.push({
                            imageUrl: item.imageUrl,
                            videoUrl: item.videoUrl,
                        });

                        setProgress((prev) => ({
                            ...prev,
                            [files[index].name]: 100,
                        }));
                    } catch (err) {
                        console.error("Upload failed:", files[index].name);
                        setProgress((prev) => ({
                            ...prev,
                            [files[index].name]: -1,
                        }));
                    }
                })
            );
            if (successUploads.length > 0) {
                setUploadStatus("saving");
                const { data } = await axios.post(
                    SAVE_API,
                    successUploads,
                    getHeaders()
                );
                setUploaded(data.data ?? []);
                setUploadStatus("completed");
                setTimeout(() => {
                    if (successUploads.length === files.length) {
                        setShowModal(false);
                    }
                }, 3000);
            } else {
                setUploadStatus("failed");
            }
        } catch (error) {
            console.error("Upload process failed:", error);
            setUploadStatus("failed");
        }
    };

    const closeModal = () => {
        if (uploadStatus === "uploading" || uploadStatus === "saving") {
            if (!window.confirm("Upload is in progress. Are you sure you want to cancel?")) {
                return;
            }
        }
        setShowModal(false);
    };

    const getTotalProgress = () => {
        const values = Object.values(progress);
        if (values.length === 0) return 0;

        const validValues = values.filter(v => v >= 0);
        if (validValues.length === 0) return 0;

        return Math.round(validValues.reduce((a, b) => a + b, 0) / validValues.length);
    };

    return (
        <div style={{ maxWidth: 600 }}>
            <h3>Bulk Upload Images / Videos</h3>

            <input
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={(e) => setFiles([...e.target.files])}
            />

            <br /><br />
            <button
                onClick={uploadAll}
                disabled={!files.length}
                style={{
                    padding: "10px 20px",
                    backgroundColor: files.length ? "#007bff" : "#cccccc",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: files.length ? "pointer" : "not-allowed",
                }}
            >
                Upload All ({files.length})
            </button>

            
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: "rgba(0,0,0,0.5)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1000,
                        }}
                        onClick={closeModal}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            style={{
                                backgroundColor: "white",
                                padding: "30px",
                                borderRadius: "12px",
                                width: "90%",
                                maxWidth: "500px",
                                maxHeight: "80vh",
                                overflowY: "auto",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                                <h3 style={{ margin: 0 }}>
                                    {uploadStatus === "uploading" && "Uploading Files..."}
                                    {uploadStatus === "saving" && "Saving to Database..."}
                                    {uploadStatus === "completed" && "Upload Complete!"}
                                    {uploadStatus === "failed" && "Upload Failed"}
                                </h3>
                                <button
                                    onClick={closeModal}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        fontSize: "24px",
                                        cursor: "pointer",
                                        color: "#666",
                                    }}
                                >
                                    ×
                                </button>
                            </div>

                            
                            <div style={{ marginBottom: "25px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                                    <span>Overall Progress</span>
                                    <span>{getTotalProgress()}%</span>
                                </div>
                                <div style={{
                                    height: "10px",
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "5px",
                                    overflow: "hidden",
                                }}>
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${getTotalProgress()}%` }}
                                        transition={{ duration: 0.3 }}
                                        style={{
                                            height: "100%",
                                            backgroundColor: uploadStatus === "failed" ? "#dc3545" :
                                                uploadStatus === "completed" ? "#28a745" : "#007bff",
                                        }}
                                    />
                                </div>
                            </div>

                            
                            <div style={{ marginBottom: "20px" }}>
                                <h4 style={{ marginBottom: "10px" }}>File Progress:</h4>
                                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                    {files.map((file, index) => (
                                        <div key={index} style={{ marginBottom: "12px" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                                                <span style={{
                                                    fontSize: "14px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "70%",
                                                }}>
                                                    {file.name}
                                                </span>
                                                <span style={{
                                                    fontSize: "14px",
                                                    color: progress[file.name] === -1 ? "#dc3545" :
                                                        progress[file.name] === 100 ? "#28a745" : "#666",
                                                }}>
                                                    {progress[file.name] === -1 ? "Failed" :
                                                        progress[file.name] === 100 ? "Done" : `${progress[file.name] || 0}%`}
                                                </span>
                                            </div>
                                            <div style={{
                                                height: "6px",
                                                backgroundColor: "#f0f0f0",
                                                borderRadius: "3px",
                                                overflow: "hidden",
                                            }}>
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${progress[file.name] === -1 ? 100 : progress[file.name] || 0}%` }}
                                                    transition={{ duration: 0.3 }}
                                                    style={{
                                                        height: "100%",
                                                        backgroundColor: progress[file.name] === -1 ? "#dc3545" :
                                                            progress[file.name] === 100 ? "#28a745" : "#007bff",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            
                            <div style={{
                                padding: "10px",
                                backgroundColor: uploadStatus === "completed" ? "#d4edda" :
                                    uploadStatus === "failed" ? "#f8d7da" : "#d1ecf1",
                                borderRadius: "5px",
                                marginBottom: "20px",
                                border: `1px solid ${uploadStatus === "completed" ? "#c3e6cb" :
                                        uploadStatus === "failed" ? "#f5c6cb" : "#bee5eb"
                                    }`,
                                color: uploadStatus === "completed" ? "#155724" :
                                    uploadStatus === "failed" ? "#721c24" : "#0c5460",
                            }}>
                                {uploadStatus === "uploading" && `Uploading ${Object.keys(progress).length} files to S3...`}
                                {uploadStatus === "saving" && "Saving uploaded files to database..."}
                                {uploadStatus === "completed" && `Successfully uploaded ${uploaded.length} files!`}
                                {uploadStatus === "failed" && "Upload failed. Please try again."}
                            </div>

                            
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                                {(uploadStatus === "completed" || uploadStatus === "failed") && (
                                    <button
                                        onClick={closeModal}
                                        style={{
                                            padding: "8px 20px",
                                            backgroundColor: "#6c757d",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "5px",
                                            cursor: "pointer",
                                        }}
                                    >
                                        Close
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

           
        </div>
    );
}